import React, { FormEvent, useCallback, useMemo } from 'react';
import { Button, Checkbox, Dropdown, Input, Modal } from '@cognitiv/cyprus-ui';
import classNames from 'classnames';
import { getBatchDefinitionItemCheckDepAll } from 'ducks/batch-definition-dep/operators';
import { getBatchDefinitionsList } from 'ducks/batch-definition-list/operators';
import { createJobParametersConnect } from 'ducks/batch-definitions/operators';
import { useAppDispatch, useAppSelector } from 'ducks/hooks';
import { selectCreateParameter, selectCreateParameterView } from 'ducks/modals/selectors';
import { setModal } from 'ducks/modals/slices';
import { selectParameterMethodsList } from 'ducks/parameter-methods-list/selectors';
import { updateSettings } from 'ducks/settings/slices';
import { selectSystemParametersList } from 'ducks/system-parameters-list/selectors';
import { ERROR_LEVEL } from 'ducks/types/common-types';
import { ProjectParameters } from 'ducks/types/store-types';
import { UnionFromArray } from 'ducks/types/type-helpers';
import { useSwitchTheme } from 'hooks/useSwitchTheme';
import { useCreateParameterFormMethods } from 'modals/create-parameter/hooks/useCreateParameterFormMethods';
import { shallowEqual } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getValidationError, handleError } from 'utils/errors';
import { getCheckBoxTheme } from 'utils/modal/helpers';

import cn from 'modals/create-parameter/CreateParameter.module.scss';

interface ParamsProps {
  batch_definition_id?: number;
}

export const CreateParameter = () => {
  const dispatch = useAppDispatch();
  const { theme } = useSwitchTheme();
  const { batch_definition_id }: ParamsProps = useParams();
  const check_box_theme = useMemo(() => getCheckBoxTheme(theme), [theme]);

  const create_parameter = useAppSelector(selectCreateParameter, shallowEqual);
  const create_parameter_view = useAppSelector(selectCreateParameterView, shallowEqual);
  const parameter_methods = useAppSelector(selectParameterMethodsList);
  const system_parameters = useAppSelector(selectSystemParametersList);

  const { form, onChange, errors, validate, onParameterMethodSelect, onSystemReferenceNameChange } =
    useCreateParameterFormMethods();

  const handleModalClose = useCallback(() => {
    dispatch(setModal({ create_parameter_view: false }));
  }, [dispatch]);

  const {
    parameter_name,
    command_line_assignment_char,
    command_line_escaped,
    command_line_ignore_name,
    is_required,
    command_line_prefix,
    reference_parameter_name,
    parameter_method_name,
    parameter_value,
  } = form;

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const is_valid = validate();

    if (!is_valid) return;

    try {
      await createJobParametersConnect(create_parameter.owner_id, form);
      if (batch_definition_id) {
        await dispatch(getBatchDefinitionsList(batch_definition_id));
        await dispatch(getBatchDefinitionItemCheckDepAll(batch_definition_id));
      }
      await dispatch(setModal({ create_parameter: { vision: false } }));
    } catch (err) {
      const error = err as Error;
      handleError({ error: error.message, ...error });
    }
    dispatch(updateSettings({ loading: false }));
  };

  return (
    <Modal
      name="create_parameter_view"
      show={create_parameter_view}
      width={500}
      padding={0}
      close_box={50}
      onClose={handleModalClose}
      standard={false}
    >
      <h2
        className={cn.header}
        style={{
          backgroundColor: theme.modal.modal_background,
          color: theme.font_color,
          borderBottom: theme.border,
        }}
      >
        Manage Parameter
      </h2>
      <form className={classNames(cn.body, cn.createParameter)}>
        <Input
          auto_focus
          label="Parameter Name"
          value={parameter_name}
          onChange={(item: string) => onChange({ parameter_name: item })}
          error_message={errors.find((error) => error.key === 'parameter_name')?.error}
          error_level={
            getValidationError<UnionFromArray<ProjectParameters>>({ errors, key: 'parameter_name' })
              ? ERROR_LEVEL.ERROR
              : undefined
          }
        />
        <Input
          label="Value"
          margin="16px 0px 0px 0px"
          value={parameter_value}
          onChange={(item: string) => onChange({ parameter_value: item })}
          error_message={errors.find((error) => error.key === 'parameter_value')?.error}
          error_level={
            getValidationError<UnionFromArray<ProjectParameters>>({ errors, key: 'parameter_value' })
              ? ERROR_LEVEL.ERROR
              : undefined
          }
        />
        <>
          <Dropdown
            label="Reference"
            options={system_parameters}
            option_key="system_parameter_name"
            value={reference_parameter_name}
            margin="16px 0px 0px 0px"
            onSelect={onSystemReferenceNameChange}
          />
          <Dropdown
            label="Method"
            options={parameter_methods}
            option_key="parameter_method_name"
            value={parameter_method_name}
            margin="16px 0px 0px 0px"
            onSelect={onParameterMethodSelect}
          />
          <>
            <div className={cn.inputs}>
              <Input
                value={command_line_prefix}
                label="Prefix"
                margin="16px 5px 0px 0px"
                width="calc(50% - 5px)"
                onChange={(item: string) => onChange({ command_line_prefix: item })}
              />
              <Input
                value={command_line_assignment_char}
                label="Assignment Character"
                margin="16px 0px 0px 5px"
                width="calc(50% - 5px)"
                onChange={(item: string) => onChange({ command_line_assignment_char: item })}
              />
            </div>
            <div className={cn.checkboxes}>
              <Checkbox
                width={null}
                onChange={(item: boolean) => onChange({ command_line_escaped: item })}
                checked={command_line_escaped}
                theme={check_box_theme}
              >
                Escape Value
              </Checkbox>
              <Checkbox
                width={null}
                onChange={(item: boolean) => onChange({ command_line_ignore_name: item })}
                checked={command_line_ignore_name}
                theme={check_box_theme}
              >
                Don't Include Name
              </Checkbox>
              <Checkbox
                width={null}
                onChange={(item: boolean) => onChange({ is_required: item })}
                checked={is_required}
                theme={check_box_theme}
              >
                Required
              </Checkbox>
            </div>
          </>
        </>
        <div className={cn.buttonContainer}>
          <Button width={115} onClick={onSubmit} variant="blue" button_size="medium" margin="16px 0px 0px 0px">
            Add
          </Button>
        </div>
      </form>
    </Modal>
  );

  // return (
  //   <Modal
  //     name="create_parameter_view"
  //     show={create_parameter_view}
  //     width={500}
  //     padding={0}
  //     close_box={50}
  //     onClose={handleModalClose}
  //     standard={false}
  //   >
  //     <h2
  //       className={cn.header}
  //       style={{
  //         backgroundColor: theme.modal.modal_background,
  //         color: theme.font_color,
  //         borderBottom: theme.border,
  //       }}
  //     >
  //       Manage Parameter
  //     </h2>
  //     <form onSubmit={handleSubmit(onSubmitOld)} className={classNames(cn.body, cn.createParameter)}>
  //       <Input
  //         auto_focus
  //         label="Parameter Name"
  //         value={parameter_name}
  //         onChange={(value: string) => onChangeOld('parameter_name', value)}
  //         error_message={errorsOld.parameter_name?.message}
  //         error_level={errorsOld.parameter_name?.message ? ERROR_LEVEL.ERROR : undefined}
  //       />
  //       <Input
  //         label="Value"
  //         margin="16px 0px 0px 0px"
  //         value={parameter_value}
  //         onChange={(value: string) => onChangeOld('parameter_value', value)}
  //         error_message={errorsOld.parameter_value?.message}
  //         error_level={errorsOld.parameter_value?.message ? ERROR_LEVEL.ERROR : undefined}
  //       />
  //       <>
  //         <Dropdown
  //           label="Reference"
  //           options={system_parameters}
  //           option_key="system_parameter_name"
  //           value={reference_parameter_name}
  //           margin="16px 0px 0px 0px"
  //           onSelect={onSystemReferenceNameChange}
  //         />
  //         <Dropdown
  //           label="Method"
  //           options={parameter_methods}
  //           option_key="parameter_method_name"
  //           value={parameter_method_name}
  //           margin="16px 0px 0px 0px"
  //           onSelect={onParameterMethodSelect}
  //         />
  //         <>
  //           <div className={cn.inputs}>
  //             <Input
  //               value={command_line_prefix}
  //               label="Prefix"
  //               margin="16px 5px 0px 0px"
  //               width="calc(50% - 5px)"
  //               onChange={(value: string) => onChangeOld('command_line_prefix', value)}
  //             />
  //             <Input
  //               value={command_line_assignment_char}
  //               label="Assignment Character"
  //               margin="16px 0px 0px 5px"
  //               width="calc(50% - 5px)"
  //               onChange={(value: string) => onChangeOld('command_line_assignment_char', value)}
  //             />
  //           </div>
  //           <div className={cn.checkboxes}>
  //             <Checkbox
  //               width={null}
  //               onClick={(value: boolean) => onChangeOld('command_line_escaped', value)}
  //               checked={command_line_escaped}
  //               theme={check_box_theme}
  //             >
  //               Escape Value
  //             </Checkbox>
  //             <Checkbox
  //               width={null}
  //               onClick={(value: boolean) => onChangeOld('command_line_ignore_name', value)}
  //               checked={command_line_ignore_name}
  //               theme={check_box_theme}
  //             >
  //               Don't Include Name
  //             </Checkbox>
  //             <Checkbox
  //               width={null}
  //               onClick={(value: boolean) => onChangeOld('is_required', value)}
  //               checked={is_required}
  //               theme={check_box_theme}
  //             >
  //               Required
  //             </Checkbox>
  //           </div>
  //         </>
  //       </>
  //       <div className={cn.buttonContainer}>
  //         <Button
  //           disabled={!isValid}
  //           width={115}
  //           variant="blue"
  //           button_size="medium"
  //           margin="16px 0px 0px 0px"
  //           type="submit"
  //         >
  //           Add
  //         </Button>
  //       </div>
  //     </form>
  //   </Modal>
  // );
};
